import { t as uiText } from '../../i18n';
import React, { useRef, useState } from 'react';
import './Day0Panel.css';
import { useWorkspaceEntitlements } from '../plans/useWorkspaceEntitlements';
import { deleteAppearanceImage, uploadAppearanceImage } from './appearanceAssets';
import {
  BUTTON_POSITION_OPTIONS,
  WIDGET_POSITION_OPTIONS,
  type AppearanceDraft,
  type AppearanceOffset,
} from './appearanceSettings';

interface AppearancePanelProps {
  workspaceId: string;
  draft: AppearanceDraft;
  saving: boolean;
  dirty: boolean;
  onChange: (next: AppearanceDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  onClose: () => void;
}

type AppearanceTab = 'button' | 'widget' | 'content';

function pickerHex(value: string, fallback: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

function ColorField({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string;
  value: string;
  fallback: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="day0-panel__label">
      {label}
      <span className="day0-panel__color-row">
        <input
          type="color"
          value={pickerHex(value, fallback)}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} />
      </span>
    </label>
  );
}

function RangeField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="day0-panel__label">
      {label} {value}px
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function OffsetFields({
  offset,
  onChange,
}: {
  offset: AppearanceOffset;
  onChange: (next: AppearanceOffset) => void;
}) {
  const axes: { key: keyof AppearanceOffset; label: string }[] = [
    { key: 'top', label: uiText('Top:') },
    { key: 'bottom', label: uiText('Bottom:') },
    { key: 'left', label: uiText('Left:') },
    { key: 'right', label: uiText('Right:') },
  ];
  return (
    <div className="day0-panel__pair">
      {axes.map((axis) => (
        <label key={axis.key} className="day0-panel__label">
          {axis.label} {offset[axis.key] > 0 ? '+' : ''}{offset[axis.key]}px
          <input
            type="range"
            min={-100}
            max={100}
            step={4}
            value={offset[axis.key]}
            onChange={(e) => onChange({ ...offset, [axis.key]: Number(e.target.value) })}
          />
        </label>
      ))}
    </div>
  );
}

const AppearancePanel: React.FC<AppearancePanelProps> = ({
  workspaceId,
  draft,
  saving,
  dirty,
  onChange,
  onSave,
  onCancel,
  onClose,
}) => {
  const { can, loading: entitlementsLoading } = useWorkspaceEntitlements();
  const canButtonImage = can('widget.buttonImage');
  const canIcon = can('widget.icon');
  const canTooltip = can('widget.tooltip');
  const canDesignColor = can('publicRag.designColor');
  const canContentText = can('publicRag.contentText');
  const showContentTab = entitlementsLoading || canContentText;
  const [tab, setTab] = useState<AppearanceTab>('content');
  const activeTab: AppearanceTab = tab === 'content' && !showContentTab ? 'button' : tab;
  const [uploading, setUploading] = useState(false);
  const buttonImageRef = useRef<HTMLInputElement>(null);
  const iconImageRef = useRef<HTMLInputElement>(null);
  const actionsDisabled = !dirty || saving;

  const upload = async (file: File, kind: 'button' | 'icon') => {
    setUploading(true);
    try {
      const previousUrl = kind === 'button' ? draft.imageUrl : draft.icon.url;
      const url = await uploadAppearanceImage(workspaceId, file, kind, previousUrl);
      if (kind === 'button') {
        onChange({ ...draft, imageUrl: url, buttonType: 'image' });
      } else {
        onChange({ ...draft, icon: { ...draft.icon, url, enabled: true } });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      alert(message || (kind === 'button'
        ? uiText('Failed to upload button image.')
        : uiText('Failed to upload the icon image.')));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (kind: 'button' | 'icon') => {
    const url = kind === 'button' ? draft.imageUrl : draft.icon.url;
    if (!url) return;
    if (!confirm(kind === 'button' ? uiText('Delete the button image?') : uiText('Delete the icon image?'))) return;
    setUploading(true);
    try {
      await deleteAppearanceImage(url);
      if (kind === 'button') {
        onChange({ ...draft, imageUrl: '' });
      } else {
        onChange({ ...draft, icon: { ...draft.icon, url: '', enabled: false } });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="day0-panel">
      <header className="day0-panel__header">
        <h2 className="day0-panel__title">{uiText('Choose the look')}</h2>
        <div className="day0-panel__header-actions">
          <button type="button" className="day0-panel__header-btn" onClick={onCancel} disabled={actionsDisabled}>
            {uiText('Cancel')}
          </button>
          <button
            type="button"
            className="day0-panel__header-btn day0-panel__header-btn--save"
            onClick={onSave}
            disabled={actionsDisabled}
          >
            {saving ? uiText('Saving…') : uiText('Save')}
          </button>
        </div>
        <button type="button" className="day0-panel__close" onClick={onClose} aria-label={uiText('Close')}>
          ×
        </button>
      </header>
      <p className="day0-panel__lead">{uiText('The button on the left updates right away. Save when you are ready.')}</p>
      <div className="day0-panel__tabs" role="tablist">
        {showContentTab && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'content'}
            className={`day0-panel__tab${activeTab === 'content' ? ' day0-panel__tab--active' : ''}`}
            onClick={() => setTab('content')}
          >
            {uiText('Display text')}
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'button'}
          className={`day0-panel__tab${activeTab === 'button' ? ' day0-panel__tab--active' : ''}`}
          onClick={() => setTab('button')}
        >
          {uiText('Button')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'widget'}
          className={`day0-panel__tab${activeTab === 'widget' ? ' day0-panel__tab--active' : ''}`}
          onClick={() => setTab('widget')}
        >
          {uiText('Widget')}
        </button>
      </div>

      {activeTab === 'button' ? (
        <>
          <fieldset className="day0-panel__group">
            <legend>{uiText('Display position')}</legend>
            <label className="day0-panel__label">
              {uiText('Base position')}
              <select
                value={draft.buttonPosition}
                onChange={(e) => onChange({ ...draft, buttonPosition: e.target.value as AppearanceDraft['buttonPosition'] })}
              >
                {BUTTON_POSITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="day0-panel__label">
              {uiText('Right-edge anchor')}
              <select
                value={draft.edge}
                onChange={(e) => onChange({ ...draft, edge: e.target.value as AppearanceDraft['edge'] })}
              >
                <option value="content">{uiText('content (default / ignore scrollbar)')}</option>
                <option value="window">{uiText('window (align to the window edge, including scrollbar width)')}</option>
              </select>
            </label>
            <p className="day0-panel__hint">{uiText('Position adjustment (-100 to +100 px)')}</p>
            <OffsetFields offset={draft.buttonOffset} onChange={(buttonOffset) => onChange({ ...draft, buttonOffset })} />
          </fieldset>

          <fieldset className="day0-panel__group">
            <legend>{uiText('Text')}</legend>
            <label className="day0-panel__label">
              {uiText('Text')}
              <input
                type="text"
                value={draft.buttonText}
                onChange={(e) => onChange({ ...draft, buttonText: e.target.value })}
              />
            </label>
            <ColorField
              label={uiText('Text color')}
              value={draft.textColor}
              fallback="#ffffff"
              onChange={(textColor) => onChange({ ...draft, textColor })}
            />
            <div className="day0-panel__pair">
              <RangeField
                label={uiText('Font size:')}
                value={draft.fontSize}
                min={8}
                max={48}
                step={1}
                onChange={(fontSize) => onChange({ ...draft, fontSize })}
              />
              <label className="day0-panel__label">
                {uiText('Font weight')}
                <select
                  value={String(draft.fontWeight)}
                  onChange={(e) => onChange({ ...draft, fontWeight: Number(e.target.value) })}
                >
                  {draft.fontWeight !== 400 && draft.fontWeight !== 700 && (
                    <option value={String(draft.fontWeight)}>{draft.fontWeight}</option>
                  )}
                  <option value="400">{uiText('Regular')}</option>
                  <option value="700">{uiText('Bold')}</option>
                </select>
              </label>
            </div>
            <label className="day0-panel__label">
              {uiText('Text direction')}
              <select
                value={draft.writingMode}
                onChange={(e) => onChange({
                  ...draft,
                  writingMode: e.target.value as AppearanceDraft['writingMode'],
                })}
              >
                <option value="horizontal">{uiText('Horizontal text')}</option>
                <option value="vertical">{uiText('Vertical text')}</option>
              </select>
            </label>
            {draft.writingMode === 'vertical' && (
              <label className="day0-panel__toggle">
                <input
                  type="checkbox"
                  checked={draft.tcyEnabled}
                  onChange={(e) => onChange({ ...draft, tcyEnabled: e.target.checked })}
                />
                {uiText('Horizontal-in-vertical')}
              </label>
            )}
          </fieldset>

          <fieldset className="day0-panel__group">
            <legend>{uiText('Background color')}</legend>
            <ColorField
              label={uiText('Background color')}
              value={draft.backgroundColor}
              fallback="#cfae91"
              onChange={(backgroundColor) => onChange({ ...draft, backgroundColor })}
            />
            <ColorField
              label={uiText('Border color')}
              value={draft.borderColor}
              fallback="#000000"
              onChange={(borderColor) => onChange({ ...draft, borderColor })}
            />
            <div className="day0-panel__pair">
              <RangeField
                label={uiText('Border width:')}
                value={draft.borderWidth}
                min={0}
                max={16}
                step={1}
                onChange={(borderWidth) => onChange({ ...draft, borderWidth })}
              />
              <RangeField
                label={uiText('Corner radius:')}
                value={draft.borderRadius}
                min={0}
                max={100}
                step={2}
                onChange={(borderRadius) => onChange({ ...draft, borderRadius })}
              />
            </div>
          </fieldset>

          <fieldset className="day0-panel__group">
            <legend>{uiText('Size:')}</legend>
            <div className="day0-panel__pair">
              <RangeField
                label={uiText('Column width:')}
                value={draft.buttonWidth}
                min={16}
                max={Math.max(240, draft.buttonWidth)}
                step={4}
                onChange={(buttonWidth) => onChange({ ...draft, buttonWidth })}
              />
              <RangeField
                label={uiText('Height:')}
                value={draft.buttonHeight}
                min={16}
                max={Math.max(240, draft.buttonHeight)}
                step={4}
                onChange={(buttonHeight) => onChange({ ...draft, buttonHeight })}
              />
            </div>
            <div className="day0-panel__pair">
              <RangeField
                label={uiText('Horizontal padding:')}
                value={draft.paddingX}
                min={0}
                max={60}
                step={1}
                onChange={(paddingX) => onChange({ ...draft, paddingX })}
              />
              <RangeField
                label={uiText('Vertical padding:')}
                value={draft.paddingY}
                min={0}
                max={40}
                step={1}
                onChange={(paddingY) => onChange({ ...draft, paddingY })}
              />
            </div>
          </fieldset>

          <fieldset className="day0-panel__group">
            <legend>{uiText('Close button style')}</legend>
            <label className="day0-panel__toggle">
              <input
                type="checkbox"
                checked={draft.showCloseButton}
                onChange={(e) => onChange({ ...draft, showCloseButton: e.target.checked })}
              />
              {uiText('Show close button')}
            </label>
            {draft.showCloseButton && (
              <>
                <ColorField
                  label={uiText('Close (×) color')}
                  value={draft.closeButton.iconColor}
                  fallback="#ffffff"
                  onChange={(iconColor) => onChange({ ...draft, closeButton: { ...draft.closeButton, iconColor } })}
                />
                <ColorField
                  label={uiText('Background color')}
                  value={draft.closeButton.backgroundColor}
                  fallback="#000000"
                  onChange={(backgroundColor) => onChange({
                    ...draft,
                    closeButton: { ...draft.closeButton, backgroundColor },
                  })}
                />
                <ColorField
                  label={uiText('Border color')}
                  value={draft.closeButton.borderColor}
                  fallback="#ffffff"
                  onChange={(borderColor) => onChange({
                    ...draft,
                    closeButton: { ...draft.closeButton, borderColor },
                  })}
                />
                <label className="day0-panel__label">
                  {uiText('Background opacity:')} {Math.round(draft.closeButton.opacity * 100)}%
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={draft.closeButton.opacity}
                    onChange={(e) => onChange({
                      ...draft,
                      closeButton: { ...draft.closeButton, opacity: Number(e.target.value) },
                    })}
                  />
                </label>
              </>
            )}
          </fieldset>

          {canButtonImage && (
            <fieldset className="day0-panel__group">
              <legend>{uiText('Button type')}</legend>
              <label className="day0-panel__label">
                {uiText('Button type')}
                <select
                  value={draft.buttonType}
                  onChange={(e) => onChange({
                    ...draft,
                    buttonType: e.target.value as AppearanceDraft['buttonType'],
                    ...(e.target.value === 'text' ? { imageMode: 'styled' as const } : {}),
                  })}
                >
                  <option value="text">{uiText('Text')}</option>
                  <option value="image">{uiText('Image')}</option>
                </select>
              </label>
              {draft.buttonType === 'image' && (
                <>
                  <div className="day0-panel__file-row">
                    <input
                      ref={buttonImageRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,.svg,.gif"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) void upload(file, 'button');
                      }}
                    />
                    <button
                      type="button"
                      className="day0-panel__header-btn"
                      disabled={uploading}
                      onClick={() => buttonImageRef.current?.click()}
                    >
                      {uiText('Select image')}
                    </button>
                    {draft.imageUrl && (
                      <button
                        type="button"
                        className="day0-panel__header-btn"
                        disabled={uploading}
                        onClick={() => { void removeImage('button'); }}
                      >
                        {uiText('Delete')}
                      </button>
                    )}
                  </div>
                  <p className="day0-panel__hint">{draft.imageUrl ? uiText('Preview') : uiText('No image set')}</p>
                  <label className="day0-panel__label">
                    {uiText('Image display mode')}
                    <select
                      value={draft.imageMode}
                      onChange={(e) => onChange({
                        ...draft,
                        imageMode: e.target.value as AppearanceDraft['imageMode'],
                      })}
                    >
                      <option value="styled">{uiText('Standard (apply button settings)')}</option>
                      <option value="image-only">{uiText('Image only (no background, rounded corners, or shadow)')}</option>
                    </select>
                  </label>
                  <label className="day0-panel__label">
                    {uiText('Image display method')}
                    <select
                      value={draft.imageFit}
                      onChange={(e) => onChange({
                        ...draft,
                        imageFit: e.target.value as AppearanceDraft['imageFit'],
                      })}
                    >
                      <option value="contain">{uiText('contain (show entire image, padding OK)')}</option>
                      <option value="cover">{uiText('cover (fill the area, cropped)')}</option>
                      <option value="fill">{uiText('fill (distortion OK)')}</option>
                    </select>
                  </label>
                </>
              )}
            </fieldset>
          )}

          {canIcon && (
            <fieldset className="day0-panel__group">
              <legend>{uiText('Icon')}</legend>
              <label className="day0-panel__toggle">
                <input
                  type="checkbox"
                  checked={draft.icon.enabled}
                  onChange={(e) => onChange({ ...draft, icon: { ...draft.icon, enabled: e.target.checked } })}
                />
                {uiText('Icon')}
              </label>
              <div className="day0-panel__file-row">
                <input
                  ref={iconImageRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.svg,.gif"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void upload(file, 'icon');
                  }}
                />
                <button
                  type="button"
                  className="day0-panel__header-btn"
                  disabled={uploading}
                  onClick={() => iconImageRef.current?.click()}
                >
                  {uiText('Select image')}
                </button>
                {draft.icon.url && (
                  <button
                    type="button"
                    className="day0-panel__header-btn"
                    disabled={uploading}
                    onClick={() => { void removeImage('icon'); }}
                  >
                    {uiText('Delete')}
                  </button>
                )}
              </div>
              {draft.icon.enabled && (
                <>
                  <RangeField
                    label={uiText('Size:')}
                    value={draft.icon.size}
                    min={8}
                    max={48}
                    step={1}
                    onChange={(size) => onChange({ ...draft, icon: { ...draft.icon, size } })}
                  />
                  <label className="day0-panel__label">
                    {uiText('Display position')}
                    <select
                      value={draft.icon.position}
                      onChange={(e) => onChange({
                        ...draft,
                        icon: { ...draft.icon, position: e.target.value as AppearanceDraft['icon']['position'] },
                      })}
                    >
                      <option value="before">{uiText('Before text')}</option>
                      <option value="after">{uiText('After text')}</option>
                    </select>
                  </label>
                  <label className="day0-panel__toggle">
                    <input
                      type="checkbox"
                      checked={draft.icon.colorEnabled}
                      onChange={(e) => onChange({
                        ...draft,
                        icon: { ...draft.icon, colorEnabled: e.target.checked },
                      })}
                    />
                    {uiText('Change icon color')}
                  </label>
                  {draft.icon.colorEnabled && (
                    <ColorField
                      label={uiText('Icon')}
                      value={draft.icon.color}
                      fallback="#ffffff"
                      onChange={(color) => onChange({ ...draft, icon: { ...draft.icon, color } })}
                    />
                  )}
                </>
              )}
            </fieldset>
          )}

          {canTooltip && (
            <fieldset className="day0-panel__group">
              <legend>{uiText('Tooltip')}</legend>
              <label className="day0-panel__toggle">
                <input
                  type="checkbox"
                  checked={draft.tooltip.enabled}
                  onChange={(e) => onChange({
                    ...draft,
                    tooltip: { ...draft.tooltip, enabled: e.target.checked },
                  })}
                />
                {uiText('Show tooltip')}
              </label>
              {draft.tooltip.enabled && (
                <>
                  <label className="day0-panel__label">
                    {uiText('Tooltip text')}
                    <textarea
                      rows={3}
                      value={draft.tooltip.text}
                      onChange={(e) => onChange({
                        ...draft,
                        tooltip: { ...draft.tooltip, text: e.target.value },
                      })}
                    />
                  </label>
                  <ColorField
                    label={uiText('Background color')}
                    value={draft.tooltip.backgroundColor}
                    fallback="#000000"
                    onChange={(backgroundColor) => onChange({
                      ...draft,
                      tooltip: { ...draft.tooltip, backgroundColor },
                    })}
                  />
                  <ColorField
                    label={uiText('Text color')}
                    value={draft.tooltip.textColor}
                    fallback="#ffffff"
                    onChange={(textColor) => onChange({
                      ...draft,
                      tooltip: { ...draft.tooltip, textColor },
                    })}
                  />
                  <label className="day0-panel__label">
                    {uiText('Text alignment')}
                    <select
                      value={draft.tooltip.textAlign}
                      onChange={(e) => onChange({
                        ...draft,
                        tooltip: {
                          ...draft.tooltip,
                          textAlign: e.target.value as AppearanceDraft['tooltip']['textAlign'],
                        },
                      })}
                    >
                      <option value="center">{uiText('Align center')}</option>
                      <option value="left">{uiText('Align left')}</option>
                    </select>
                  </label>
                  <RangeField
                    label={uiText('Width:')}
                    value={draft.tooltip.maxWidth}
                    min={80}
                    max={240}
                    step={10}
                    onChange={(maxWidth) => onChange({
                      ...draft,
                      tooltip: { ...draft.tooltip, maxWidth },
                    })}
                  />
                </>
              )}
            </fieldset>
          )}
        </>
      ) : activeTab === 'widget' ? (
        <>
          {canDesignColor && (
            <fieldset className="day0-panel__group">
              <legend>{uiText('Main design color')}</legend>
              <ColorField
                label={uiText('Main design color')}
                value={draft.mainColor}
                fallback="#cfae91"
                onChange={(mainColor) => onChange({ ...draft, mainColor })}
              />
              <ColorField
                label={uiText('Link text color')}
                value={draft.linkTextColor}
                fallback="#cfae91"
                onChange={(linkTextColor) => onChange({ ...draft, linkTextColor })}
              />
              <ColorField
                label={uiText('Main text color')}
                value={draft.mainTextColor}
                fallback="#334155"
                onChange={(mainTextColor) => onChange({ ...draft, mainTextColor })}
              />
              <ColorField
                label={uiText('Subtext color')}
                value={draft.subTextColor}
                fallback="#64748b"
                onChange={(subTextColor) => onChange({ ...draft, subTextColor })}
              />
              <ColorField
                label={uiText('Keyword text color')}
                value={draft.keywordTextColor}
                fallback="#64748b"
                onChange={(keywordTextColor) => onChange({ ...draft, keywordTextColor })}
              />
            </fieldset>
          )}

          <fieldset className="day0-panel__group">
            <legend>{uiText('Widget position')}</legend>
            <label className="day0-panel__label">
              {uiText('Widget position')}
              <select
                value={draft.widgetPosition}
                onChange={(e) => onChange({
                  ...draft,
                  widgetPosition: e.target.value as AppearanceDraft['widgetPosition'],
                })}
              >
                {WIDGET_POSITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <p className="day0-panel__hint">{uiText('Position of the widget that opens when the button is clicked')}</p>
            <RangeField
              label={uiText('Widget width:')}
              value={draft.widgetWidth}
              min={300}
              max={Math.max(800, draft.widgetWidth)}
              step={10}
              onChange={(widgetWidth) => onChange({ ...draft, widgetWidth })}
            />
            <p className="day0-panel__hint">{uiText('Position adjustment (-100 to +100 px)')}</p>
            <OffsetFields offset={draft.widgetOffset} onChange={(widgetOffset) => onChange({ ...draft, widgetOffset })} />
          </fieldset>

          <fieldset className="day0-panel__group">
            <legend>{uiText('Other settings')}</legend>
            <label className="day0-panel__toggle">
              <input
                type="checkbox"
                checked={draft.closeOnOutsideClick}
                onChange={(e) => onChange({ ...draft, closeOnOutsideClick: e.target.checked })}
              />
              {uiText('Close when clicking outside the widget')}
            </label>
            <p className="day0-panel__hint">{uiText('Closes the widget when you click outside it (on the page area)')}</p>
            <label className="day0-panel__toggle">
              <input
                type="checkbox"
                checked={draft.dimBackground}
                onChange={(e) => onChange({ ...draft, dimBackground: e.target.checked })}
              />
              {uiText('Dim the background')}
            </label>
            <p className="day0-panel__hint">{uiText('Dims the entire page while the widget is open')}</p>
          </fieldset>
        </>
      ) : (
        <fieldset className="day0-panel__group">
          <legend>{uiText('Display text')}</legend>
          <label className="day0-panel__label">
            {uiText('Title')}
            <textarea
              value={draft.contentTitle}
              maxLength={100}
              rows={3}
              placeholder={uiText('Enter a content title')}
              onChange={(e) => onChange({ ...draft, contentTitle: e.target.value })}
            />
            <span className="day0-panel__hint">{uiText('Characters:')} {draft.contentTitle.length}/100</span>
          </label>
          <label className="day0-panel__label">
            {uiText('Subtitle')}
            <textarea
              value={draft.contentSubtitle}
              maxLength={200}
              rows={3}
              placeholder={uiText('Enter a content subtitle')}
              onChange={(e) => onChange({ ...draft, contentSubtitle: e.target.value })}
            />
            <span className="day0-panel__hint">{uiText('Characters:')} {draft.contentSubtitle.length}/200</span>
          </label>
          <label className="day0-panel__label">
            {uiText('Placeholder')}
            <input
              type="text"
              value={draft.contentPlaceholder}
              maxLength={100}
              placeholder={uiText('Enter placeholder text for the field')}
              onChange={(e) => onChange({ ...draft, contentPlaceholder: e.target.value })}
            />
            <span className="day0-panel__hint">{uiText('Characters:')} {draft.contentPlaceholder.length}/100</span>
          </label>
        </fieldset>
      )}
    </section>
  );
};

export default AppearancePanel;
